<script lang="ts" setup>
import { computed, inject, ref, watch } from "vue";
import { EAddressType, IYesNoEnum } from "../ApiInterfaces";
import { SITEURL, USEREMAIL } from "@libTs/cms.globals";
import Cookies from "js-cookie";
import {
    IAddressBlock,
    ISalesOrderAddedResponse,
    ISalesOrderRequest,
} from "../interfaces/cart.interfaces";
import { CountryCodes } from "../CountryCodes";
import {
    IOrderDone,
    ICookieCheckoutAddresses,
} from "../interfaces/checkout.interfaces";
import axios from "axios";
import { OverlaysService } from "../services/overlays.service";
import { EOverlay } from "../EOverlay";
import { TranslationService } from "../services/translation.service";
import { CartService } from "../services/cart.service";
import { vInputText as vInputTextDir } from "../directives/v-input-text";

const vInputText = vInputTextDir;
const overlaysSvc = inject<OverlaysService>("overlaysSvc")!;
const cartSvc = inject<CartService>("cartSvc")!;
const t = inject<TranslationService>("translationSvc")!.t;

import CartOverview from "./cart-overview.vue";

const { billingAddresses, shippingAddresses, webPermission, priceDisclaimer } =
    defineProps({
        billingAddresses: {
            type: Array<IAddressBlock>,
            required: true,
        },
        shippingAddresses: {
            type: Array<IAddressBlock>,
            required: true,
        },
        webPermission: {
            type: Number,
            required: true,
        },
        priceDisclaimer: {
            type: String,
            required: true,
        },
    });

const titles = ["Choose a title", "Mr.", "Mrs.", "Ms.", "Dr.", "Prof."];

const EXPIRES = new Date();
EXPIRES.setDate(EXPIRES.getDate() + 14); // now + 14 days

const checkoutmodel = ref({
    billingAddress: {
        AddressType: EAddressType.B,
        Default: IYesNoEnum.N,
        AddressId: "",
        Street: "",
        StreetNo: "",
        State: "",
        ZipCode: "",
        City: "",
        Country: "CH",
        AddressName2: "",
        AddressName3: "",
        Address2obj: {
            Company: "",
            Department: "",
            County: "",
        },
        Address3obj: {
            Title: "",
            FirstName: "",
            LastName: "",
        },
    } as IAddressBlock,
    shipToCode: "",
    shippingAddress: {
        AddressType: EAddressType.S,
        Default: IYesNoEnum.N,
        AddressId: "",
        Street: "",
        StreetNo: "",
        State: "",
        ZipCode: "",
        City: "",
        Country: "CH",
        AddressName2: "",
        AddressName3: "",
        Address2obj: {
            Company: "",
            Department: "",
            County: "",
        },
        Address3obj: {
            Title: "",
            FirstName: "",
            LastName: "",
        },
    } as IAddressBlock,
    Comments: "",
    onlyPDF: false,
    OrderRefNr: "",
});

// ===========
// CONFIG
const CHECKOUTCOOKIENAME = "checkout" + USEREMAIL;
const COOKIESETTINGS = {
    path: "/",
    expires: EXPIRES,
};
// ===========

const isLoading = ref(false);
const orderProcessed = ref(false);
const Responses = ref<Array<IOrderDone>>([]);
const dropBillAddress = ref(false);
const dropShipAddress = ref(false);
const dropTitle = ref(false);
const dropCountry = ref(false);
const countries = CountryCodes;

const acceptTerms = ref(false);

const mergedBillingAddresses = ref<Array<IAddressBlock>>(
    ((billingAddresses: Array<IAddressBlock>): Array<IAddressBlock> => {
        // load cookie addresses
        const addresses = getCheckoutAddrCookie();
        if (typeof addresses === "object" && addresses && addresses.billing) {
            billingAddresses.push(addresses.billing);
        }

        return billingAddresses;
    })(billingAddresses),
);

const mergedShippingAddresses = ref<Array<IAddressBlock>>(
    ((shippingAddresses: Array<IAddressBlock>) => {
        // load cookie addresses
        const addresses = getCheckoutAddrCookie();

        if (typeof addresses === "object" && addresses && addresses.shipping) {
            shippingAddresses.push(addresses.shipping);
        }

        return shippingAddresses;
    })(shippingAddresses),
);

function getCheckoutAddrCookie(): ICookieCheckoutAddresses | null {
    const cookie = Cookies.get(CHECKOUTCOOKIENAME);

    if (cookie) {
        return JSON.parse(cookie);
    } else {
        return null;
    }
}

function delCheckoutAddrCookie() {
    Cookies.remove(CHECKOUTCOOKIENAME, COOKIESETTINGS);
}

const getDefaultAddress = (addresses: IAddressBlock[]) => {
    if (typeof addresses === "undefined" || addresses.length <= 0) {
        return [];
    }

    let defaultAddress: IAddressBlock | null = null;
    for (const a of addresses) {
        if (a.Default === IYesNoEnum.Y) {
            defaultAddress = a;
        }
    }
    if (defaultAddress && defaultAddress.Default === IYesNoEnum.Y) {
        return defaultAddress;
    }
    return addresses[0];
};

const copyAddressToFirstShippingAddressDefaults = (address: IAddressBlock) => {
    let defaultShippingAddr: IAddressBlock = {} as IAddressBlock;

    address.Address2 = "";

    defaultShippingAddr = Object.assign(defaultShippingAddr, address);
    defaultShippingAddr.AddressId = "";
    defaultShippingAddr.AddressType = EAddressType.S;
    defaultShippingAddr.Default = IYesNoEnum.N;
    defaultShippingAddr.Address3obj = {
        FirstName: "",
        LastName: "",
        Title: titles[0],
    };
    defaultShippingAddr.Address2obj = {
        Company: "",
        Department: "",
        County: "",
    };

    mergedShippingAddresses.value[0] = defaultShippingAddr;
};

const getDropdownAddressName = (address: IAddressBlock) => {
    return (
        address.AddressId +
        ", " +
        address.AddressName2 +
        (address.AddressName3 ? ", " + address.AddressName3 : "")
    );
};

const isValid = computed(() => {
    const billingAddress = checkoutmodel.value.billingAddress;
    const shippingAddress = checkoutmodel.value.shippingAddress;

    return (
        ((billingAddress.AddressId.length > 0 &&
            billingAddress.AddressId !== "CSV") ||
            ((billingAddress.AddressId.length <= 0 ||
                billingAddress.AddressId === "CSV") &&
                billingAddress.Street.length > 0 &&
                billingAddress.City.length > 0 &&
                billingAddress.ZipCode.length > 0 &&
                billingAddress.Country.length > 0)) && // = valid shipping address
        ((shippingAddress.AddressId.length > 0 &&
            shippingAddress.AddressId !== "CSV") || // shipping address from CSV
            (shippingAddress.AddressId === "CSV" &&
                shippingAddress.AddressName2.length > 0 &&
                shippingAddress.Street.length > 0 &&
                shippingAddress.City.length > 0 &&
                shippingAddress.ZipCode.length > 0 &&
                shippingAddress.Country.length > 0) || // new shipping address from form fields
            (typeof shippingAddress.Address3obj !== "undefined" &&
                shippingAddress.Address3obj.FirstName.length > 0 &&
                shippingAddress.Address3obj.LastName.length > 0 &&
                shippingAddress.Street.length > 0 &&
                shippingAddress.City.length > 0 &&
                shippingAddress.ZipCode.length > 0 &&
                shippingAddress.Country.length > 0))
    );
});

const sendOrder = () => {
    // copy addresses to new objects, use the copied object only for the order
    // this prevents UI changes visible for the customer
    let billingAddress = {} as IAddressBlock;
    billingAddress = Object.assign(
        billingAddress,
        checkoutmodel.value.billingAddress,
    );
    let shippingAddress = {} as IAddressBlock;
    shippingAddress = Object.assign(
        shippingAddress,
        checkoutmodel.value.shippingAddress,
    );

    // check for validity of fields
    if (!isValid.value) {
        console.info("sendOrder: Fields not valid.");
        alert("Error: Missing or empty fields in billing or shipping address.");
        return;
    }

    // Monolot
    checkoutmodel.value.Comments =
        cartSvc.getMonolotCheckoutText() + checkoutmodel.value.Comments;
    // END Monolot

    orderProcessed.value = false;
    isLoading.value = true;
    Responses.value = [];

    if (cartSvc.cartObj.carts.length > 0) {
        const orderRequests: Array<ISalesOrderRequest> =
            cartSvc.getOrderRequestObject(
                billingAddress,
                shippingAddress.AddressId,
                shippingAddress,
                checkoutmodel.value.Comments,
                checkoutmodel.value.OrderRefNr,
            );
        console.log(JSON.stringify(orderRequests, undefined, " "));

        if (!checkoutmodel.value.onlyPDF) {
            axios
                .post<Array<ISalesOrderAddedResponse>>(
                    SITEURL + "actions/sapShop/order/add-sales-order",
                    {
                        debuglevel: 0, // no debug
                        orders: orderRequests,
                    },
                    {
                        responseType: "json",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                    },
                )
                .then(
                    // success
                    (response: { data: Array<ISalesOrderAddedResponse> }) => {
                        const data = response.data;
                        console.info("Order response", response);

                        const responses: Array<IOrderDone> = []; // for output in frontend
                        const carts = cartSvc.cartObj.carts;

                        if (data) {
                            let deleteShippingBillingAddress = true;
                            for (let i = 0; i < data.length; ++i) {
                                const cart = carts[i];
                                const resp = data[i];

                                // skip for csv single order with thp
                                // (response: array with 2 times the exact same response )
                                if (
                                    i === 0 &&
                                    data.length === 2 &&
                                    data[0].Value.length &&
                                    data[0].Value === data[1].Value
                                ) {
                                    if (data[0].ResponseStatus === 200) {
                                        // clear default cart
                                        cartSvc.clearCart(carts[0]);
                                    }
                                    continue;
                                } else {
                                    responses.push({
                                        orderReference: cart.orderReference,
                                        orderName: cart.orderName,
                                        statusCode: resp.ResponseStatus,
                                    });
                                }

                                if (
                                    resp.Value !== "" &&
                                    resp.ResponseStatus === 200
                                ) {
                                    cartSvc.clearCart(cart);

                                    console.info("Order successfully sent.");
                                } else if (resp.ResponseStatus === 403) {
                                    // clear cart but dont overwrite cookie
                                    cartSvc.clearCart(cart);

                                    console.warn(
                                        "Order not sent, login session has expired.",
                                    );

                                    deleteShippingBillingAddress = false;
                                } else if (
                                    resp.ResponseStatus === 555 ||
                                    resp.ResponseMessage === "MAINTENANCEMODE"
                                ) {
                                    // Shop is in maintenance mode, redirect to homepage
                                    window.location.href = SITEURL;
                                    return;
                                } else {
                                    deleteShippingBillingAddress = false;
                                }
                            }
                            if (deleteShippingBillingAddress) {
                                delCheckoutAddrCookie();
                            }
                            cartSvc.updateTotalPrice();

                            Responses.value = responses;
                        } else {
                            Responses.value = [];
                            console.warn(
                                "Error while processing order: Order has not been added.",
                            );
                        }
                    },
                    // error
                    (response: Response) => {
                        console.warn("Error: No response from server.");
                        console.info(response);
                        Responses.value = [];
                    },
                )
                .finally(() => {
                    isLoading.value = false;
                    orderProcessed.value = true;
                });
        } else {
            axios
                .post<Array<ISalesOrderAddedResponse>>(
                    SITEURL + "actions/sapShop/order/create-pdf",
                    {
                        debuglevel: 0, // no debug
                        orders: orderRequests,
                    },
                    {
                        responseType: "blob",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                    },
                )
                .then(
                    // success
                    (response) => {
                        const data = response.data;
                        console.info("Order response", response);

                        const link = document.createElement("a");
                        // create a blobURI pointing to our Blob
                        link.href = URL.createObjectURL(data);
                        link.download = "offer.pdf";
                        // some browser needs the anchor to be in the doc
                        document.body.append(link);
                        link.click();
                        link.remove();
                        // in case the Blob uses a lot of memory
                        setTimeout(() => URL.revokeObjectURL(link.href), 7000);

                        Responses.value = [
                            {
                                statusCode: 201,
                                orderReference: "",
                                orderName: "PDF",
                            },
                        ];
                    },
                    // error
                    (response: Response) => {
                        console.warn("Error: No response from server.");
                        console.info(response);
                        Responses.value = [];
                    },
                )
                .finally(() => {
                    isLoading.value = false;
                    orderProcessed.value = true;
                });
        }
    } else {
        alert("Order not sent. Your cart is empty.");
        Responses.value = [];
    }
};

checkoutmodel.value.billingAddress = getDefaultAddress(
    mergedBillingAddresses.value,
) as IAddressBlock;

copyAddressToFirstShippingAddressDefaults(checkoutmodel.value.billingAddress);

checkoutmodel.value.shippingAddress = getDefaultAddress(
    mergedShippingAddresses.value,
) as IAddressBlock;
</script>
<template>
    <div class="">
        <div class="">
            <div class="">
                <div class="">
                    <div class="h1 mb-8">Your order</div>

                    <div
                        v-if="
                            cartSvc.cartObj &&
                            cartSvc.cartObj.isOrderForOtherUser
                        "
                        v-cloak
                    >
                        <p style="color: red">
                            This order will be sent as the customer "{{
                                cartSvc.cartObj.customerEmail
                            }}".
                            <br />
                        </p>
                    </div>

                    <form name="form" @submit.prevent="sendOrder()" novalidate>
                        <div class="border-b border-grey-200 border-dotted">
                            <h2 class="py-6 border-b border-grey-200 mb-3">
                                {{ t("Shipping address") }}
                            </h2>

                            <div class="font-bold text-f5 py-3">
                                Your shipping address
                            </div>

                            <div
                                class="pb-6 relative"
                                v-show="!dropShipAddress"
                            >
                                <div class="">
                                    <div class="addressblock">
                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .AddressName2
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .AddressName2
                                            }}
                                        </div>

                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .Address2obj?.Company.length
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .Address2obj?.Company
                                            }}
                                        </div>

                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .Address2obj?.Department
                                                    .length
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .Address2obj?.Department
                                            }}
                                        </div>

                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .AddressName3 &&
                                                checkoutmodel.shippingAddress
                                                    .AddressName3.length
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .AddressName3
                                            }}
                                        </div>

                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .Address3obj?.Title !==
                                                    'Choose a title' ||
                                                checkoutmodel.shippingAddress
                                                    .Address3obj?.FirstName
                                                    .length ||
                                                checkoutmodel.shippingAddress
                                                    .Address3obj?.LastName
                                                    .length
                                            "
                                        >
                                            <span
                                                v-if="
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Address3obj?.Title !==
                                                    'Choose a title'
                                                "
                                            >
                                                {{
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Address3obj?.Title
                                                }}
                                                &hairsp;
                                            </span>
                                            <span
                                                v-if="
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Address3obj?.FirstName
                                                        .length
                                                "
                                            >
                                                {{
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Address3obj?.FirstName
                                                }}
                                                &hairsp;
                                            </span>
                                            <span
                                                v-if="
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Address3obj?.LastName
                                                        .length
                                                "
                                            >
                                                {{
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Address3obj?.LastName
                                                }}
                                            </span>
                                        </div>

                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .County &&
                                                checkoutmodel.shippingAddress
                                                    .County.length
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .County
                                            }}
                                        </div>
                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .State
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .State
                                            }}
                                        </div>
                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .Street
                                            "
                                        >
                                            <span>{{
                                                checkoutmodel.shippingAddress
                                                    .Street
                                            }}</span>
                                            <span
                                                v-if="
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .StreetNo
                                                "
                                            >
                                                {{
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .StreetNo
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .Building
                                            "
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .Building
                                            }}
                                        </div>
                                        <div
                                            v-if="
                                                checkoutmodel.shippingAddress
                                                    .City ||
                                                checkoutmodel.shippingAddress
                                                    .ZipCode ||
                                                checkoutmodel.shippingAddress
                                                    .Country
                                            "
                                        >
                                            <span>{{
                                                checkoutmodel.shippingAddress
                                                    .Country
                                                    ? checkoutmodel.shippingAddress.Country?.trim() +
                                                      "-"
                                                    : ""
                                            }}</span>
                                            <span
                                                >{{
                                                    checkoutmodel.shippingAddress.ZipCode?.trim()
                                                }}
                                                {{
                                                    checkoutmodel.shippingAddress.City?.trim()
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    @click="dropShipAddress = true"
                                    class="btn-cb btn-small mt-4 py-2 px-4"
                                    type="button"
                                >
                                    <i class="fal fa-pen"></i>
                                    Change address
                                </button>
                            </div>

                            <div class="mt-10" v-show="dropShipAddress">
                                <ul
                                    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 items-stretch"
                                >
                                    <template
                                        v-for="s in mergedShippingAddresses"
                                    >
                                        <li
                                            class="shadow-md p-7 mb-4 cursor-pointer rounded-m border border-grey-200 flex flex-row gap-5"
                                            @click="
                                                checkoutmodel.shippingAddress =
                                                    s
                                            "
                                            v-if="
                                                s ==
                                                    mergedShippingAddresses[0] &&
                                                checkoutmodel.shippingAddress
                                            "
                                        >
                                            <div
                                                class="w-5 h-5 rounded-full border-2 border-black shrink-0"
                                            >
                                                <div
                                                    v-if="
                                                        checkoutmodel
                                                            .shippingAddress
                                                            .AddressId ==
                                                        s.AddressId
                                                    "
                                                    class="w-3 h-3 bg-primary rounded-full m-[2px]"
                                                ></div>
                                            </div>
                                            <div>New shipping address</div>
                                        </li>
                                        <li
                                            class="shadow-md p-7 mb-4 cursor-pointer rounded-m border border-grey-200 flex flex-row gap-5"
                                            @click="
                                                checkoutmodel.shippingAddress =
                                                    s
                                            "
                                            v-if="
                                                s != mergedShippingAddresses[0]
                                            "
                                        >
                                            <div
                                                class="w-5 h-5 rounded-full border-2 border-black shrink-0"
                                            >
                                                <div
                                                    v-if="
                                                        checkoutmodel
                                                            .shippingAddress
                                                            .AddressId ==
                                                        s.AddressId
                                                    "
                                                    class="w-3 h-3 bg-primary rounded-full m-[2px]"
                                                ></div>
                                            </div>
                                            <div>
                                                <span>{{ s.AddressId }}</span
                                                >,
                                                <span>{{
                                                    s.AddressName2
                                                }}</span>
                                                <div v-if="s.AddressName3">
                                                    {{ s.AddressName3 }}
                                                </div>
                                                <div v-if="s.County">
                                                    {{ s.County }}
                                                </div>
                                                <div v-if="s.Building">
                                                    {{ s.Building }}
                                                </div>
                                                <div v-if="s.State">
                                                    {{ s.State }}
                                                </div>
                                                <div v-if="s.Street">
                                                    <span>{{ s.Street }}</span>
                                                    <span v-if="s.StreetNo">{{
                                                        s.StreetNo
                                                    }}</span>
                                                </div>
                                                <div
                                                    v-if="
                                                        s.City &&
                                                        s.ZipCode &&
                                                        s.Country
                                                    "
                                                >
                                                    <span>{{
                                                        s.Country
                                                            ? s.Country.trim() +
                                                              "-"
                                                            : ""
                                                    }}</span>
                                                    <span>{{
                                                        s.ZipCode.trim()
                                                    }}</span>
                                                    <span>{{
                                                        s.City.trim()
                                                    }}</span>
                                                </div>
                                            </div>
                                        </li>
                                    </template>
                                </ul>
                            </div>

                            <div
                                v-if="
                                    checkoutmodel.shippingAddress.AddressId ==
                                    mergedShippingAddresses[0].AddressId
                                "
                                v-show="dropShipAddress"
                                class="flex flex-col gap-4 max-w-[650px]"
                            >
                                <div class="">
                                    <label class="text-f7"> Company </label>
                                    <input
                                        class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                        type="text"
                                        v-model="
                                            checkoutmodel.shippingAddress
                                                .Address2obj!.Company
                                        "
                                        autocomplete="organization"
                                    />
                                </div>

                                <div class="">
                                    <label class="text-f7"> Department </label>
                                    <input
                                        class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                        type="text"
                                        v-model="
                                            checkoutmodel.shippingAddress
                                                .Address2obj!.Department
                                        "
                                    />
                                </div>

                                <div class="">
                                    <label class="text-f7">County</label>
                                    <input
                                        class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                        type="text"
                                        v-model="
                                            checkoutmodel.shippingAddress
                                                .Address2obj!.County
                                        "
                                        autocomplete="address-level1"
                                    />
                                </div>

                                <div class="">
                                    <label class="text-f7">Title</label>

                                    <div class="dropdown-cb relative">
                                        <div
                                            class="px-5 py-3 w-full bg-grey-50 rounded-s relative"
                                            @click="dropTitle = !dropTitle"
                                        >
                                            {{
                                                checkoutmodel.shippingAddress
                                                    .Address3obj?.Title
                                            }}
                                            <i
                                                class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2 right-6"
                                            ></i>
                                        </div>
                                        <ul
                                            v-if="dropTitle"
                                            class="bg-white w-full shadow flex flex-col max-h-60 overflow-y-auto z-10 absolute gap-4 p-6 rounded-s"
                                        >
                                            <li
                                                v-for="t in titles"
                                                class="cursor-pointer"
                                                @click="
                                                    dropTitle = false;
                                                    checkoutmodel.shippingAddress.Address3obj!.Title =
                                                        t;
                                                "
                                            >
                                                {{ t }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div class="">
                                    <label class="text-f7"> First name </label>
                                    <input
                                        class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                        type="text"
                                        v-model="
                                            checkoutmodel.shippingAddress
                                                .Address3obj!.FirstName
                                        "
                                        autocomplete="given-name"
                                        required
                                    />
                                </div>
                                <div class="">
                                    <label class="text-f7"> Last name </label>
                                    <input
                                        class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                        type="text"
                                        v-model="
                                            checkoutmodel.shippingAddress
                                                .Address3obj!.LastName
                                        "
                                        autocomplete="family-name"
                                        required
                                    />
                                </div>

                                <div class="">
                                    <label class="text-f7">Street</label>
                                    <input
                                        class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                        type="text"
                                        v-model="
                                            checkoutmodel.shippingAddress.Street
                                        "
                                        autocomplete="address-line1"
                                        required
                                    />
                                </div>

                                <div class="grid grid-cols-3 gap-4">
                                    <div class="">
                                        <label class="text-f7">
                                            Zip Code
                                        </label>
                                        <input
                                            class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                            type="text"
                                            v-model="
                                                checkoutmodel.shippingAddress
                                                    .ZipCode
                                            "
                                            autocomplete="postal-code"
                                            required
                                        />
                                    </div>
                                    <div class="col-span-2">
                                        <label class="text-f7"> City </label>
                                        <input
                                            class="px-5 py-3 w-full bg-grey-50 rounded-s"
                                            type="text"
                                            v-model="
                                                checkoutmodel.shippingAddress
                                                    .City
                                            "
                                            autocomplete="address-level2"
                                            required
                                        />
                                    </div>
                                </div>

                                <div class="">
                                    <label class="text-f7"> Country </label>
                                    <div class="dropdown-cb relative">
                                        <div
                                            class="px-5 py-3 w-full bg-grey-50 rounded-s relative"
                                            @click="dropCountry = !dropCountry"
                                        >
                                            {{
                                                countries[
                                                    checkoutmodel
                                                        .shippingAddress
                                                        .Country as keyof typeof countries
                                                ]
                                            }}
                                            <i
                                                class="fas fa-chevron-down text-primary absolute top-1/2 -translate-y-1/2 right-6"
                                            ></i>
                                        </div>
                                        <ul
                                            v-if="dropCountry"
                                            class="bg-white w-full shadow flex flex-col max-h-60 overflow-y-auto z-10 absolute gap-4 p-6 rounded-s"
                                        >
                                            <li
                                                class="cursor-pointer"
                                                v-for="(
                                                    cname, ccode
                                                ) in countries"
                                                @click="
                                                    dropCountry = false;
                                                    checkoutmodel.shippingAddress.Country =
                                                        ccode;
                                                "
                                            >
                                                {{ cname }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <button
                                @click="dropShipAddress = false"
                                class="btn-cb btn-small mt-8 mb-8 py-2 px-4"
                                type="button"
                                v-if="dropShipAddress"
                            >
                                Apply changes
                            </button>
                        </div>

                        <div
                            class="border-b border-grey-200 border-dotted pb-6"
                        >
                            <div class="font-bold text-f5 py-3">
                                Order reference
                            </div>

                            <div class="">
                                <label class="text-f7 block mb-2"
                                    >Please enter your reference here:</label
                                >
                                <input
                                    class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s h-10 max-w-[650px]"
                                    type="text"
                                    autocomplete="off"
                                    v-model="checkoutmodel.OrderRefNr"
                                />
                            </div>
                        </div>

                        <h2 class="py-6 border-b border-grey-200 mb-3 mt-8">
                            {{ t("Billing address") }}
                        </h2>
                        <div class="font-bold text-f5 py-3">
                            Your billing address
                        </div>
                        <div
                            class="pb-6 relative border-b border-grey-200 border-dotted"
                            v-show="!dropBillAddress"
                        >
                            <div class="">
                                <div class="addressblock">
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress
                                                .AddressName2.length
                                        "
                                    >
                                        {{
                                            checkoutmodel.billingAddress
                                                .AddressName2
                                        }}
                                    </div>
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress
                                                .AddressName3 &&
                                            checkoutmodel.billingAddress
                                                .AddressName3.length
                                        "
                                    >
                                        {{
                                            checkoutmodel.billingAddress
                                                .AddressName3
                                        }}
                                    </div>
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress
                                                .County &&
                                            checkoutmodel.billingAddress.County
                                                .length
                                        "
                                    >
                                        {{
                                            checkoutmodel.billingAddress.County
                                        }}
                                    </div>
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress
                                                .Building
                                        "
                                    >
                                        {{
                                            checkoutmodel.billingAddress
                                                .Building
                                        }}
                                    </div>
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress.State
                                        "
                                    >
                                        {{ checkoutmodel.billingAddress.State }}
                                    </div>
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress.Street
                                        "
                                    >
                                        <span>{{
                                            checkoutmodel.billingAddress.Street
                                        }}</span>
                                        <span
                                            v-if="
                                                checkoutmodel.billingAddress
                                                    .StreetNo
                                            "
                                        >
                                            {{
                                                checkoutmodel.billingAddress
                                                    .StreetNo
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            checkoutmodel.billingAddress.City ||
                                            checkoutmodel.billingAddress
                                                .ZipCode ||
                                            checkoutmodel.billingAddress.Country
                                        "
                                    >
                                        <span>{{
                                            checkoutmodel.billingAddress.Country
                                                ? checkoutmodel.billingAddress.Country.trim() +
                                                  "-"
                                                : ""
                                        }}</span>
                                        <span
                                            >{{
                                                checkoutmodel.billingAddress.ZipCode.trim()
                                            }}
                                            {{
                                                checkoutmodel.billingAddress.City.trim()
                                            }}</span
                                        >
                                    </div>
                                    <span>{{ USEREMAIL }}</span
                                    ><br />
                                </div>
                            </div>

                            <button
                                @click="dropBillAddress = true"
                                class="btn-cb btn-small mt-4 py-2 px-4"
                                type="button"
                            >
                                <i class="fal fa-pen"></i>
                                Change address
                            </button>
                        </div>

                        <div
                            class="mt-6 pb-6 relative border-b border-grey-200 border-dotted"
                            v-show="dropBillAddress"
                        >
                            <ul
                                class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 items-stretch"
                            >
                                <li
                                    v-for="b in mergedBillingAddresses"
                                    class="shadow-md p-7 mb-4 cursor-pointer rounded-m border border-grey-200 flex flex-row gap-5"
                                    @click="
                                        dropBillAddress = false;
                                        checkoutmodel.billingAddress = b;
                                    "
                                >
                                    <div
                                        class="w-5 h-5 rounded-full border-2 border-black shrink-0"
                                    >
                                        <div
                                            v-if="
                                                checkoutmodel.billingAddress
                                                    .AddressId == b.AddressId
                                            "
                                            class="w-3 h-3 bg-primary rounded-full m-[2px]"
                                        ></div>
                                    </div>
                                    <div>
                                        <span>{{ b.AddressId }}</span
                                        >,
                                        <span>{{ b.AddressName2 }}</span>
                                        <div v-if="b.AddressName3">
                                            {{ b.AddressName3 }}
                                        </div>
                                        <div v-if="b.County">
                                            {{ b.County }}
                                        </div>
                                        <div v-if="b.Building">
                                            {{ b.Building }}
                                        </div>
                                        <div v-if="b.State">
                                            {{ b.State }}
                                        </div>
                                        <div v-if="b.Street">
                                            <span>{{ b.Street }}</span>
                                            <span v-if="b.StreetNo">{{
                                                b.StreetNo
                                            }}</span>
                                        </div>
                                        <div
                                            v-if="
                                                b.City || b.ZipCode || b.Country
                                            "
                                        >
                                            <span>{{
                                                b.Country
                                                    ? b.Country.trim() + "-"
                                                    : ""
                                            }}</span>
                                            <span>{{ b.ZipCode.trim() }}</span>
                                            <span>{{ b.City.trim() }}</span>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                            <button
                                @click="dropBillAddress = false"
                                class="btn-cb btn-small mt-8 mb-8 py-2 px-4"
                                type="button"
                                v-if="dropBillAddress"
                            >
                                Apply changes
                            </button>
                        </div>

                        <div
                            class="pb-6 border-b border-grey-200 border-dotted"
                        >
                            <div class="font-bold text-f5 py-3">Comment</div>

                            <textarea
                                class="input-text-input !px-5 !py-3 w-full bg-grey-50 rounded-s h-32 max-w-[650px]"
                                id="comment"
                                rows="5"
                                cols="4"
                                type="textarea"
                                v-model="checkoutmodel.Comments"
                                style="min-height: 150px"
                                autocomplete="nope"
                            ></textarea>
                        </div>

                        <div class="">
                            <h2 class="py-6 border-b border-grey-200 mb-3 mt-8">
                                Order summary
                            </h2>

                            <cart-overview
                                price-disclaimer="The actual amount may vary. Please check the order confirmation (email)."
                            />
                        </div>

                        <!-- Checkmark for accepting terms and conditions -->
                        <div class="pt-8 pb-12">
                            <label class="inline-flex items-center mt-6">
                                <input
                                    type="checkbox"
                                    class="form-checkbox h-5 w-5"
                                    v-model="acceptTerms"
                                />
                                <span class="ml-3">
                                    By placing this order, I accept the
                                    <a
                                        href="/terms-of-use"
                                        target="_blank"
                                        class="underline"
                                    >
                                        General Terms and Conditions
                                    </a>
                                    and acknowledge the
                                    <a
                                        href="/privacy-policy"
                                        target="_blank"
                                        class="underline"
                                        >privacy policy</a
                                    >
                                    of Chemie Brunschwig.
                                </span>
                            </label>
                        </div>

                        <div class="flex justify-end">
                            <div
                                v-if="webPermission != 0"
                                class=""
                                :class="{ 'loading-animation': isLoading }"
                                style="display: inline-block"
                            >
                                <button
                                    type="submit"
                                    class="btn-cb"
                                    :class="{ 'loading-animation': isLoading }"
                                    :disabled="
                                        (checkoutmodel.shippingAddress.AddressId
                                            .length <= 0 &&
                                            !isValid) ||
                                        isLoading ||
                                        !acceptTerms
                                    "
                                >
                                    Place binding order
                                    <i class="fas fa-arrow-right ml-2"></i>
                                </button>
                            </div>

                            <!--<div
                                :class="{ 'loading-animation': isLoading }"
                                style="display: inline-block"
                            >
                                <input
                                    @click="checkoutmodel.onlyPDF = true"
                                    type="submit"
                                    class="submit oldbtn-cb"
                                    :class="{ 'loading-animation': isLoading }"
                                    :value="t('Create offer PDF')"
                                    :disabled="
                                        (checkoutmodel.shippingAddress.AddressId
                                            .length <= 0 &&
                                            !isValid) ||
                                        isLoading
                                    "
                                />
                            </div>-->
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
    <!-- Order Info -->
    <div class="overlay" :class="{ 'overlay-show': orderProcessed }">
        <div class="overlay-content content-full-width">
            <div class="mb-8" v-for="response in Responses">
                <div class="col-sm-offset-2 col-sm-8 col-lg-offset-3 col-lg-6">
                    <div class="h2" v-if="response.orderReference">
                        {{ t("Order ref.") }}:<br />
                        <span>{{ response.orderReference }}</span>
                        <span>{{ response.orderName ?? "" }}</span>
                    </div>
                    <div
                        class="h2"
                        v-if="!response.orderReference && Responses.length > 1"
                    >
                        {{ t("Default order") }}
                    </div>
                    <div class="h2" v-if="response.statusCode === 403">
                        {{ t("CHECKOUT_UNAVAILABLE_LOGIN_AGAIN") }}
                    </div>
                    <div class="h2" v-else-if="response.statusCode === 200">
                        {{ t("CHECKOUT_ORDER_SUCCESSFUL") }}
                    </div>
                    <div class="h2" v-else-if="response.statusCode === 201">
                        {{ t("PDF_CREATION_SUCCESSFUL") }}
                    </div>
                    <div class="h2" v-else>
                        {{ t("CHECKOUT_PROBLEM_WITH_ORDER") }}
                    </div>
                </div>
            </div>
            <div class="mt-4">
                <a class="btn-cb" :href="SITEURL">
                    Back to home
                    <i class="fas fa-arrow-right ml-2"></i>
                </a>
            </div>
        </div>
    </div>
</template>
